
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNodes } from '@/hooks/useNodes';
import { useUpdateEdge } from '@/hooks/useCrossDisciplinaryEdges';
import { CheckCircle, XCircle } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import EdgeFilters from './EdgeFilters';

interface PendingApprovalListProps {
  edges: any[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filterRelationship: string;
  onRelationshipFilterChange: (value: string) => void;
  onEdgeUpdated: () => void;
}

const PendingApprovalList: React.FC<PendingApprovalListProps> = ({
  edges,
  searchTerm,
  onSearchChange,
  filterRelationship,
  onRelationshipFilterChange,
  onEdgeUpdated
}) => {
  const { data: allNodes } = useNodes();
  const updateEdge = useUpdateEdge();

  const publishedNodes = allNodes?.filter(node => node.status === 'published') || [];

  const relationshipTypes = [
    { value: 'prerequisite', label: 'Prerequisite', color: 'bg-white text-black' },
    { value: 'related', label: 'Related', color: 'bg-white text-green-800' },
    { value: 'deepens', label: 'Deepens', color: 'bg-white text-black' },
    { value: 'critiques', label: 'Critiques', color: 'bg-red-100 text-red-800' },
    { value: 'applies', label: 'Applies', color: 'bg-white text-black' },
    { value: 'exemplifies', label: 'Exemplifies', color: 'bg-indigo-100 text-indigo-800' }
  ];

  const getRelationshipColor = (type: string) => {
    return relationshipTypes.find(rt => rt.value === type)?.color || 'bg-white text-black';
  };

  const getNodeById = (nodeId?: string | null) => {
    if (!nodeId) return null;
    return publishedNodes.find(node => node.id === nodeId);
  };

  const handleApproveEdge = async (edgeId: string) => {
    try {
      await updateEdge.mutateAsync({
        id: edgeId,
        metadata: { status: 'approved' }
      });
      toast({ title: 'Success', description: 'Connection approved' });
      onEdgeUpdated();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve connection', variant: 'destructive' });
    }
  };

  const handleRejectEdge = async (edgeId: string) => {
    try {
      await updateEdge.mutateAsync({
        id: edgeId,
        metadata: { status: 'rejected' }
      });
      toast({ title: 'Success', description: 'Connection rejected' });
      onEdgeUpdated();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject connection', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Pending Approval ({edges.length})</span>
          <EdgeFilters
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            filterRelationship={filterRelationship}
            onRelationshipFilterChange={onRelationshipFilterChange}
          />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {edges.length === 0 ? (
          <p className="text-black text-center py-8">
            No connections pending approval
          </p>
        ) : (
          <div className="space-y-4">
            {edges.map((edge) => {
              const sourceNode = getNodeById(edge.source_node_id);
              const targetNode = getNodeById(edge.target_node_id);
              return (
                <div key={edge.id} className="p-4 border rounded-lg hover:bg-muted/50">
                  <div className="flex items-center justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{sourceNode?.title || 'Unknown Source'}</span>
                        <Badge className={getRelationshipColor(edge.relationship_type)}>
                          {edge.relationship_type}
                        </Badge>
                        <span className="font-medium">{targetNode?.title || 'Unknown Target'}</span>
                      </div>
                      {edge.description && (
                        <p className="text-sm text-black">{edge.description}</p>
                      )}
                      <div className="flex gap-2 text-xs text-black">
                        <span>Source: {sourceNode?.disciplines?.name || 'Unknown'}</span>
                        <span>•</span>
                        <span>Target: {targetNode?.disciplines?.name || 'Unknown'}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleApproveEdge(edge.id)}
                        disabled={updateEdge.isPending}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectEdge(edge.id)}
                        disabled={updateEdge.isPending}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PendingApprovalList;
